import { action, observable, makeObservable, runInAction } from "mobx";
import EmployeeAPI from "../api/EmployeeAPI";
import { Employee, Resetpassword } from "../../../models/Employees/Employee";
import { GetEmployeeParams } from "../../../models/Employees/GetEmployeeParams";
import { EmployeeDetails, Team } from "../../../models/Employees/EmployeeDetails";
import { EmployeePermisson } from "../../../models/Employees/EmployeePermisson";
import { EmployeeTeam } from "../../../models/Employees/EmployeeTeam";
import { PermissionProject } from "../../../models/Employees/PermissionProject";
import { TeamWithPermissions } from "../../../models/Employees/TeamWithPermissions";
import { SortType } from "../../../models/SortType";

export class EmployeeStore {
  employeeAPI: EmployeeAPI;
  limit: number;
  total_pages: number;
  total_count: number;
  employees: Employee[];
  employeeDetails?: Employee;
  editedEmployees?: Employee;
  error: string | null = null;
  teamPermissions: EmployeePermisson[] = [];
  teamsWithPermissions: TeamWithPermissions[] = [];
  loadingPermissionTeamIds: number[] = [];
  private loadedPermissionTeamIds = new Set<number>();
  private permissionLoads = new Map<string, Promise<void>>();
  private permissionCatalogVersion = 0;
  private employeeListRequestId = 0;
  getTeam: Team[] = [];
  adminPermissionList: EmployeePermisson[] = [];
  employeeData: EmployeeDetails[] = [];
  permissionProject: PermissionProject = "swageazy";

  constructor() {
    this.employeeAPI = new EmployeeAPI();
    this.employees = [];
    this.teamPermissions = [];
    this.teamsWithPermissions = [];
    this.loadingPermissionTeamIds = [];
    this.loadedPermissionTeamIds.clear();
    this.permissionLoads.clear();
    this.permissionCatalogVersion += 1;
    this.getTeam = [];
    this.adminPermissionList = [];
    this.employeeData = [];
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.permissionProject = "swageazy";

    makeObservable(this, {
      employees: observable,
      getEmployee: action,
      editEmployee: action,
      error: observable,
      teamPermissions: observable,
      teamsWithPermissions: observable,
      loadingPermissionTeamIds: observable,
      getTeam: observable,
      adminPermissionList: observable,
      employeeDetails: observable,
      editedEmployees: observable,
      permissionProject: observable,
      removeEmployeeDetails: action,
      reset: action,
    });
  }

  reset = () => {
    this.employees = [];
    this.teamPermissions = [];
    this.teamsWithPermissions = [];
    this.loadingPermissionTeamIds = [];
    this.loadedPermissionTeamIds.clear();
    this.permissionLoads.clear();
    this.permissionCatalogVersion += 1;
    this.getTeam = [];
    this.adminPermissionList = [];
    this.employeeData = [];
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.permissionProject = "swageazy";
  };

  setLoadingAndError = action((errorMessage: string | null) => {
    this.error = errorMessage;
  });

  setPermissionProject = action((project: PermissionProject) => {
    this.permissionProject = project;
  });

  fetchEmployee = async (params: GetEmployeeParams) => {
    try {
      this.employees = [];
      const response = await this.employeeAPI.fetchEmployee(params);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.employees = response.list;

      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getEmployee = async (params: GetEmployeeParams) => {
    const requestId = ++this.employeeListRequestId;
    try {
      this.employees = [];
      const response = await this.employeeAPI.getEmployee(params);
      if (requestId !== this.employeeListRequestId) {
        return response;
      }
      runInAction(() => {
        this.limit = response.limit;
        this.total_pages = response.total_pages;
        this.total_count = response.total_count;
        this.employees = response.list ?? [];
      });

      return response;
    } catch (error: any) {
      throw error;
    }
  };

  editEmployee = async (employeeId: number, employee: Employee) => {
    try {
      const response = await this.employeeAPI.editEmployee({
        ...employee,
        project: this.permissionProject,
      });
      if (response.success && this.employees) {
        const index = this.employees.findIndex(
          (item) => item.id === employeeId
        );
        if (index !== -1 && employee) {
          this.employees[index] = employee;
        }
      }
      if (response.success) {
        const saved =
          response.result && typeof response.result === "object"
            ? { ...employee, ...response.result, id: employeeId }
            : { ...employee, id: employeeId };
        this.employeeDetails = saved;
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getEmployeeDetails = async (
    adminId: number,
    project: PermissionProject = this.permissionProject,
  ) => {
    try {
      const response = await this.employeeAPI.getAdminDetails(adminId, project);
      this.employeeDetails = response.result as Employee;
      this.permissionProject = project;
    } catch (error: any) {
      throw error;
    }
  };

  searchEmployees = async (searchText: string): Promise<Employee[]> => {
    try {
      const response = await this.employeeAPI.searchEmployees({
        page_no: 1,
        limit: 20,
        search_text: searchText,
        sortBy: "name",
        sortType: SortType.ASC,
        active: [1],
      });
      return response.list || [];
    } catch (error: any) {
      throw error;
    }
  };

  loadEmployeeCopySource = async (
    adminId: number,
    project: PermissionProject = this.permissionProject,
  ): Promise<Employee> => {
    try {
      const response = await this.employeeAPI.getAdminDetails(adminId, project);
      return response.result as Employee;
    } catch (error: any) {
      throw error;
    }
  };

  removeEmployeeDetails = () => {
    this.employeeDetails = undefined;
    this.permissionProject = "swageazy";
    this.teamPermissions = [];
    this.teamsWithPermissions = [];
    this.loadingPermissionTeamIds = [];
    this.loadedPermissionTeamIds.clear();
    this.permissionLoads.clear();
    this.permissionCatalogVersion += 1;
  };

  getTeamPermission = async (
    employeeId: number,
    project: PermissionProject = this.permissionProject,
  ) => {
    try {
      const response = await this.employeeAPI.teamPermission(
        employeeId,
        project,
      );
      this.teamPermissions = response.result as EmployeePermisson[];
    } catch (error: any) {
      throw error;
    }
  };

  /**
   * Loads the teams this admin may assign, without their permissions.
   * Clears any permissions already fetched for the previous project.
   */
  loadEligibleTeams = async (
    project: PermissionProject = this.permissionProject,
  ): Promise<TeamWithPermissions[]> => {
    let catalogVersion = 0;
    runInAction(() => {
      this.permissionCatalogVersion += 1;
      catalogVersion = this.permissionCatalogVersion;
      this.permissionLoads.clear();
    });
    try {
      const response = await this.employeeAPI.fetchEligibleTeams(project);
      const teams = ((response.result || []) as { id: number; name: string }[])
        .map((team) => ({
          id: Number(team.id),
          name: String(team.name || ""),
          permissions: [],
        }))
        .filter((team) => team.id > 0);
      runInAction(() => {
        if (catalogVersion !== this.permissionCatalogVersion) return;
        this.permissionProject = project;
        this.loadedPermissionTeamIds.clear();
        this.loadingPermissionTeamIds = [];
        this.teamsWithPermissions = teams;
      });
      return teams;
    } catch (error: any) {
      throw error;
    }
  };

  /**
   * Loads permissions for the given teams and merges them into the eligible-team list.
   * Teams already loaded for this project are skipped.
   */
  loadPermissionsForTeams = async (
    teamIds: number[],
    project: PermissionProject = this.permissionProject,
  ): Promise<void> => {
    const requested = Array.from(
      new Set(teamIds.map((teamId) => Number(teamId)).filter((teamId) => teamId > 0)),
    );
    const eligible = new Set(
      this.teamsWithPermissions.map((team) => Number(team.id)),
    );
    const missing = requested.filter(
      (teamId) => eligible.has(teamId) && !this.loadedPermissionTeamIds.has(teamId),
    );
    if (!missing.length) return;

    const key = `${project}:${missing.slice().sort((a, b) => a - b).join(",")}`;
    const inflight = this.permissionLoads.get(key);
    if (inflight) return inflight;

    const catalogVersion = this.permissionCatalogVersion;
    const job = (async () => {
      runInAction(() => {
        if (catalogVersion !== this.permissionCatalogVersion) return;
        const loading = new Set(this.loadingPermissionTeamIds);
        missing.forEach((teamId) => loading.add(teamId));
        this.loadingPermissionTeamIds = Array.from(loading);
      });
      try {
        const response = await this.employeeAPI.fetchTeamPermissions(
          missing,
          project,
        );
        const fetched = (response.result || []) as TeamWithPermissions[];
        runInAction(() => {
          if (
            catalogVersion !== this.permissionCatalogVersion ||
            this.permissionProject !== project
          ) {
            return;
          }
          const byId = new Map(
            fetched.map((team) => [Number(team.id), team.permissions || []]),
          );
          this.teamsWithPermissions = this.teamsWithPermissions.map((team) => {
            const teamId = Number(team.id);
            if (!missing.includes(teamId)) return team;
            return {
              ...team,
              permissions: (byId.get(teamId) || []).map((permission) => ({
                role_id: Number(permission.role_id),
                role: String(permission.role || ""),
              })),
            };
          });
          missing.forEach((teamId) => this.loadedPermissionTeamIds.add(teamId));
        });
      } catch (error) {
        throw error;
      } finally {
        runInAction(() => {
          if (catalogVersion !== this.permissionCatalogVersion) return;
          const stillLoading = new Set(missing);
          this.loadingPermissionTeamIds = this.loadingPermissionTeamIds.filter(
            (teamId) => !stillLoading.has(teamId),
          );
        });
        this.permissionLoads.delete(key);
      }
    })();

    this.permissionLoads.set(key, job);
    return job;
  };

  loadTeamsWithPermissions = async (
    project: PermissionProject = this.permissionProject,
  ): Promise<TeamWithPermissions[]> => {
    return this.loadEligibleTeams(project);
  };

  loadEmployeePermissionsForProject = async (
    employeeId: number,
    project: PermissionProject,
  ) => {
    try {
      this.permissionProject = project;
      await this.getEmployeeDetails(employeeId, project);
      await this.loadEligibleTeams(project);
      const teamIds = (this.employeeDetails?.teams ?? []).map((team) =>
        Number(team.id),
      );
      await this.loadPermissionsForTeams(teamIds, project);
    } catch (error: any) {
      throw error;
    }
  };

  getAllTeam = async () => {
    try {
      const response = await this.employeeAPI.getAllTeam();
      this.getTeam = response.result as EmployeeTeam[];
    } catch (error: any) {
      throw error;
    }
  };

  addEmployee = async (employeeDetails: Employee) => {
    try {
      const response = await this.employeeAPI.addEmployee({
        ...employeeDetails,
        project: this.permissionProject,
      });
      if (response.success && response.result && typeof response.result === "object") {
        this.employeeDetails = {
          ...employeeDetails,
          ...response.result,
        };
      }
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  resetPassword = async (email: Resetpassword) => {
    try {
      const response = await this.employeeAPI.resetPassword(email);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  deleteEmployee = async (employeeId: number) => {
    try {
      const response = await this.employeeAPI.deleteEmployee(employeeId);
      return response;
    } catch (error: any) {
      throw error;
    }
  };
}
